"use client";

import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const HOLD_DURATION_MS = 3000;
const LETTER_STAGGER_S = 0.055;
const LETTER_DURATION_S = 0.5;
const EXIT_DURATION_S = 0.45;

const CJK_PATTERN = /[\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]/;

export type WrapUnit = {
  text: string;
  /** Whether a breaking space precedes this unit (word gap in the phrase). */
  spaceBefore: boolean;
  /** Index in `text` where the suffix begins, or null when it has none. */
  suffixStart: number | null;
};

/**
 * Splits a phrase into wrap units so long phrases can break across lines on
 * narrow screens instead of clipping: word-level for spaced text,
 * character-level for CJK (which has no spaces). The suffix is glued to the
 * final unit so punctuation never wraps onto a line of its own.
 */
export function buildWrapUnits(phrase: string, suffix = ""): WrapUnit[] {
  const units: WrapUnit[] = [];

  for (const word of phrase.split(" ")) {
    if (!word) continue;
    if (CJK_PATTERN.test(word)) {
      for (const [index, char] of [...word].entries()) {
        units.push({
          text: char,
          spaceBefore: index === 0 && units.length > 0,
          suffixStart: null,
        });
      }
    } else {
      units.push({ text: word, spaceBefore: units.length > 0, suffixStart: null });
    }
  }

  if (suffix) {
    if (units.length === 0) {
      units.push({ text: suffix, spaceBefore: false, suffixStart: 0 });
    } else {
      const last = units[units.length - 1];
      last.suffixStart = [...last.text].length;
      last.text += suffix;
    }
  }

  return units;
}

export type CollectionsRotatingWordProps = {
  phrases: string[];
  /** Rendered immediately after the word so punctuation hugs it. */
  suffix?: string;
};

/**
 * Rotating headline word that sizes to its current content. Each phrase is
 * revealed left-to-right with a staggered fade (letters ease in from a soft
 * blur), holds, then fades out before the next phrase reveals. Long phrases
 * wrap at word boundaries so they never clip on narrow viewports.
 */
export function CollectionsRotatingWord({
  phrases,
  suffix,
}: Readonly<CollectionsRotatingWordProps>) {
  const shouldReduceMotion = useReducedMotion();
  const [phraseIndex, setPhraseIndex] = useState(0);

  const phrasesKey = phrases.join("\u0000");
  const safePhrases = useMemo(
    () => (phrasesKey ? phrasesKey.split("\u0000") : [""]),
    [phrasesKey],
  );
  const currentPhrase = safePhrases[phraseIndex] ?? "";

  const wrapUnits = useMemo(
    () => buildWrapUnits(currentPhrase, suffix),
    [currentPhrase, suffix],
  );

  useEffect(() => {
    if (shouldReduceMotion || safePhrases.length <= 1) {
      return;
    }

    const interval = globalThis.setInterval(() => {
      setPhraseIndex((index) => (index + 1) % safePhrases.length);
    }, HOLD_DURATION_MS);

    return () => globalThis.clearInterval(interval);
  }, [safePhrases.length, shouldReduceMotion]);

  if (shouldReduceMotion) {
    return (
      <span>
        <span className="text-gail-primary">{currentPhrase}</span>
        {suffix ? <span className="text-black">{suffix}</span> : null}
      </span>
    );
  }

  return (
    <span>
      <AnimatePresence mode="wait">
        <motion.span
          key={phraseIndex}
          aria-hidden="true"
          className="text-gail-primary"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: LETTER_STAGGER_S },
            },
            exit: {
              opacity: 0,
              transition: { duration: EXIT_DURATION_S, ease: "easeIn" },
            },
          }}
        >
          {wrapUnits.map((unit, unitIndex) => (
            <span key={`${phraseIndex}-${unitIndex}`}>
              {unit.spaceBefore ? " " : null}
              <span className="inline-block whitespace-nowrap">
                {[...unit.text].map((char, charIndex) => (
                  <motion.span
                    key={`${phraseIndex}-${unitIndex}-${charIndex}`}
                    className={
                      unit.suffixStart !== null && charIndex >= unit.suffixStart
                        ? "inline-block text-black"
                        : "inline-block"
                    }
                    variants={{
                      hidden: { visibility: "hidden" },
                      visible: {
                        visibility: "visible",
                        transition: {
                          duration: LETTER_DURATION_S,
                          ease: "easeOut",
                        },
                      },
                    }}
                  >
                    {char}
                  </motion.span>
                ))}
              </span>
            </span>
          ))}
        </motion.span>
      </AnimatePresence>
      <span className="sr-only">
        {currentPhrase}
        {suffix}
      </span>
    </span>
  );
}
